import { GridRow, GridRowId, GridRowModel, GridRowProps, useGridApiContext } from '@mui/x-data-grid-premium'
import Link from 'next/link'
import { createContext, useContext } from 'react'
import {
  dataGridGroupingRowVariant,
  dataGridRowNotClickableAttribute,
  dataGridRowVariantAttribute,
  type OmneaDataGridRowVariant,
} from '@omnea-app/omui/dataGridClasses'
import { useEditingRowsContext } from './RowEditingContext'
import { isGroupRow } from './utils/isGroupRow'

export interface RowVariantProp {
  // Method syntax is deliberate: it makes `row` bivariant, so a consumer can type its resolver with its own
  // row type rather than the grid's loose GridRowModel. Must also be a stable reference — it reaches every
  // row via slotProps, so a new identity each render re-renders the whole grid. Hoist it to module scope,
  // or useCallback if it closes over state.
  getRowVariant?(row: GridRowModel): OmneaDataGridRowVariant | undefined
}

export type RowClickableProp = {
  isRowClickable?(row: GridRowModel): boolean
}

// MUI's extension point for props reaching the row slot, which is how DataGridBase forwards getRowVariant.
declare module '@mui/x-data-grid' {
  interface RowPropsOverrides {
    getRowVariant?: RowVariantProp['getRowVariant']
    isRowClickable?: RowClickableProp['isRowClickable']
  }
}

export const RowLinkContext = createContext<((id: GridRowId) => string | null) | undefined>(undefined)

export const LinkableGridRow = ({
  getRowVariant,
  isRowClickable,
  ...rowProps
}: GridRowProps & RowVariantProp & RowClickableProp) => {
  const apiRef = useGridApiContext()
  const { currentlyEditingRow } = useEditingRowsContext()
  const getRowLink = useContext(RowLinkContext)

  // Group rows expand on click; never wrap them in a row link. Server-grouping group rows carry
  // a `group` node type rather than MUI's `auto-generated-row` id, so check both.
  const isGroup = isGroupRow(apiRef, rowProps.rowId)

  const variant = isGroup ? dataGridGroupingRowVariant : getRowVariant?.(rowProps.row)
  const isNotClickable = !isGroup && isRowClickable?.(rowProps.row) === false
  const gridRow = (
    <GridRow
      {...rowProps}
      {...(variant ? { [dataGridRowVariantAttribute]: variant } : {})}
      {...(isNotClickable ? { [dataGridRowNotClickableAttribute]: true } : {})}
    />
  )

  if (isGroup || isNotClickable) return gridRow

  const rowLink = getRowLink?.(rowProps.rowId)

  if (typeof rowLink !== 'string' || currentlyEditingRow === rowProps.rowId.toString()) {
    return gridRow
  }

  return <Link href={rowLink}>{gridRow}</Link>
}
